import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";

const STORAGE_KEY = "coachconnect_beta_disclaimer_dismissed_v1";

/** Persistent-but-dismissible banner shown site-wide during private beta.
 * Explains that for the first ~3 months of operation, AI-agent-sourced
 * data (rosters, jobs, CIR scores, matches) requires human verification
 * before it can be treated as real-time source-of-truth. Dismissal is
 * remembered in localStorage so users don't see it every page load.
 */
export default function BetaDisclaimer() {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(true);
  const inAdminHQ = pathname.startsWith("/hq");
  // Never show the beta disclaimer at the top of the claim funnel — it's a
  // trust-killer at the exact moment a scouted coach decides to convert.
  const inClaimFunnel = pathname.startsWith("/claim") || pathname.startsWith("/listing/claim") || pathname.startsWith("/unsubscribe");

  useEffect(() => {
    setDismissed(localStorage.getItem(STORAGE_KEY) === "true");
  }, []);

  if (dismissed || inAdminHQ || inClaimFunnel) return null;

  const onDismiss = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setDismissed(true);
  };

  return (
    <div
      className="w-full bg-amber-500/10 border-b border-amber-500/40 text-amber-100"
      data-testid="beta-disclaimer-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-start sm:items-center gap-3 text-xs sm:text-sm">
        <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 text-amber-400 mt-0.5 sm:mt-0" />
        <p className="flex-1 leading-snug">
          <span className="font-semibold tracking-wide uppercase text-amber-300 mr-1.5">
            Private Beta
          </span>
          <span className="text-amber-400/60 mr-1.5">·</span>
          Coach profiles, CIR scores, and job data are being continually
          verified during our first three months of operation. Until our AI
          agents and data pipelines reach steady-state real-time accuracy,
          treat all results as directional — always double-check contact info
          and credentials before acting.
        </p>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded-md p-1 hover:bg-amber-500/20 focus:outline-none focus:ring-2 focus:ring-amber-400/60"
          aria-label="Dismiss disclaimer"
          data-testid="beta-disclaimer-dismiss-button"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
